import { registerTermUIElement, type HtmlAction } from "../src";
import pkg from "../package.json";

const VERSION = pkg.version;

registerTermUIElement();

type DemoTermElement = HTMLElement & {
  render: () => void;
};

// ---------------------------------------------------------------------------
// Example catalog
// ---------------------------------------------------------------------------

type Example = {
  name: string;
  category: string;
  format: "html" | "markdown" | "json";
  width: number;
  source: string;
};

const EXAMPLES: Example[] = [
  // -- Layout --
  {
    name: "Dashboard",
    category: "layout",
    format: "html",
    width: 72,
    source: `<section gap="1">
  <article title="deploy v2.4.1">
    <progress label="build" value="100" max="100"></progress>
    <progress label="tests" value="94" max="100"></progress>
    <progress label="cover" value="82" max="100"></progress>
  </article>

  <figure height="4" fill>4,7,5,12,9,14,8,11</figure>

  <pre class="language-typescript">
const config = {
  port: 8443,
  tls: true,
};
  </pre>

  <nav>
    <button action="deploy">deploy</button>
    <button action="rollback">rollback</button>
    <a href="https://github.com/TwoAbove/1ch">docs</a>
  </nav>
</section>`,
  },
  {
    name: "Grid layout",
    category: "layout",
    format: "html",
    width: 72,
    source: `<section gap="1">
  <hr label="system overview">

  <div cols="2" gap="1" row-gap="1">
    <article title="cpu">
      <progress label="core 0" value="72" max="100"></progress>
      <progress label="core 1" value="45" max="100"></progress>
      <progress label="core 2" value="89" max="100"></progress>
      <progress label="core 3" value="31" max="100"></progress>
    </article>

    <article title="memory">
      <progress label="heap" value="6.2" max="8"></progress>
      <progress label="rss" value="1.8" max="4"></progress>
      <progress label="swap" value="0.3" max="2"></progress>
    </article>

    <article title="network">
      <p>eth0  up  1.2 Gbps</p>
      <p>lo    up  --</p>
    </article>

    <article title="disk">
      <progress label="/dev/sda1" value="220" max="512"></progress>
      <progress label="/dev/sdb1" value="78" max="256"></progress>
    </article>
  </div>

  <footer left=" 4 cores | 8 GB RAM" right="uptime 31d 07h " bg="#1a1a2e" color="white"></footer>
</section>`,
  },
  {
    name: "Nested boxes",
    category: "layout",
    format: "html",
    width: 64,
    source: `<section gap="1">
  <article title="server config">
    <nav widths="30,30">
      <article title="production">
        <p>host: api.prod.io</p>
        <p>port: 443</p>
        <p>tls: enabled</p>
      </article>
      <article title="staging">
        <p>host: api.stage.io</p>
        <p>port: 8443</p>
        <p>tls: enabled</p>
      </article>
    </nav>
  </article>

  <nav>
    <button action="deploy-prod">deploy prod</button>
    <button action="deploy-stage">deploy stage</button>
  </nav>
</section>`,
  },

  // -- Data --
  {
    name: "Data table",
    category: "data",
    format: "html",
    width: 72,
    source: `<section gap="1">
  <h3>Service Health</h3>

  <table>
    <thead>
      <tr>
        <th>Service</th>
        <th>Status</th>
        <th>Uptime</th>
        <th>Latency</th>
        <th>RPS</th>
      </tr>
    </thead>
    <tbody>
      <tr><td>api-gateway</td><td>healthy</td><td>14d 03h</td><td>12ms</td><td>1,247</td></tr>
      <tr><td>auth-svc</td><td>healthy</td><td>14d 03h</td><td>8ms</td><td>892</td></tr>
      <tr><td>db-primary</td><td>healthy</td><td>31d 07h</td><td>3ms</td><td>3,401</td></tr>
      <tr><td>cache-redis</td><td>degraded</td><td>23d 11h</td><td>340ms</td><td>1,587</td></tr>
      <tr><td>worker-queue</td><td>healthy</td><td>07d 12h</td><td>45ms</td><td>456</td></tr>
    </tbody>
  </table>

  <footer left=" 4/5 healthy" right="last check: 12s ago " bg="#1a1a2e" color="white"></footer>
</section>`,
  },
  {
    name: "Charts",
    category: "data",
    format: "html",
    width: 72,
    source: `<section gap="1">
  <nav>
    <article title="requests/s">
      <figure height="6" fill color="cyan" axis-color="brightBlack">4,7,5,12,9,14,8,11,6,13,10,15</figure>
    </article>
    <article title="error rate %">
      <figure height="6" fill color="red" axis-color="brightBlack">1,0,2,1,0,0,3,1,0,1,0,2</figure>
    </article>
  </nav>

  <article title="throughput (24h)">
    <figure height="5" fill color="green" axis-color="brightBlack">8,12,15,22,28,35,42,38,31,25,18,14,11,9,7,12,18,24,31,38,42,35,28,20</figure>
  </article>
</section>`,
  },
  {
    name: "Progress bars",
    category: "data",
    format: "html",
    width: 60,
    source: `<section gap="1">
  <article title="build pipeline">
    <progress label="compile" value="100" max="100" color="green"></progress>
    <progress label="lint" value="100" max="100" color="green"></progress>
    <progress label="test" value="67" max="100" color="yellow"></progress>
    <progress label="bundle" value="0" max="100" color="brightBlack"></progress>
    <progress label="deploy" value="0" max="100" color="brightBlack"></progress>
  </article>

  <article title="storage quotas">
    <progress label="S3 bucket" value="4.7" max="10" color="cyan"></progress>
    <progress label="EBS vol" value="180" max="500"></progress>
    <progress label="RDS" value="95" max="100" color="red"></progress>
  </article>
</section>`,
  },

  // -- Code --
  {
    name: "Code block",
    category: "code",
    format: "html",
    width: 60,
    source: `<section gap="1">
  <pre class="language-typescript">
import { serve } from "bun";

const server = serve({
  port: 3000,
  fetch(req) {
    const url = new URL(req.url);
    if (url.pathname === "/api") {
      return Response.json({ ok: true });
    }
    return new Response("Not found", {
      status: 404,
    });
  },
});

console.log(server.port);
  </pre>
</section>`,
  },
  // -- Documents --
  {
    name: "Markdown",
    category: "documents",
    format: "markdown",
    width: 64,
    source: `# Release Notes v2.4.0

## Breaking Changes

- \`createServer()\` now returns a \`Promise\`
- Removed obsolete \`config.experimental\` flag

## New Features

- Added WebSocket support via \`ws://\` protocol
- File upload streaming with backpressure
- New CLI flag: \`--inspect\` for debugging

## Bug Fixes

1. Fixed memory leak in connection pool
2. Corrected UTF-8 handling in multipart parser
3. Resolved race condition in graceful shutdown

> Migration guide available at docs.example.com/migrate

\`\`\`typescript
// Before
const server = createServer(handler);

// After
const server = await createServer(handler);
\`\`\`

---

**Full changelog:** see CHANGELOG.md`,
  },
  {
    name: "JSON tree",
    category: "documents",
    format: "json",
    width: 60,
    source: JSON.stringify({
      name: "1ch",
      version: VERSION,
      type: "module",
      exports: {
        ".": "./dist/index.js",
        "./style.css": "./dist/style.css",
      },
      dependencies: {},
      devDependencies: {
        bun: "^1.2.0",
        typescript: "^5.7.0",
      },
      scripts: {
        build: "tsup",
        dev: "bun run demo/server.ts",
        preview: "NODE_ENV=production bun run demo/server.ts",
      },
    }, null, 2),
  },

  // -- Widgets --
  {
    name: "Interactive controls",
    category: "widgets",
    format: "html",
    width: 56,
    source: `<section gap="1">
  <hr label="actions">

  <nav>
    <button action="start" color="green">start</button>
    <button action="stop" color="red">stop</button>
    <button action="restart" color="yellow">restart</button>
  </nav>

  <hr label="links">

  <ul>
    <li>Click buttons above to see events fire</li>
    <li>Links also dispatch events</li>
  </ul>

  <nav>
    <a href="https://github.com/TwoAbove/1ch">GitHub</a>
    <a href="https://npmjs.com/package/1ch">npm</a>
    <a href="/docs">Docs</a>
  </nav>

  <hr>

  <footer left=" ready" right="v${VERSION} " bg="#1a1a2e" color="white"></footer>
</section>`,
  },

  // -- Media --
  {
    name: "Images",
    category: "media",
    format: "html",
    width: 72,
    source: `<section gap="1">
  <h3>Media Support</h3>

  <img src="https://picsum.photos/200/100" alt="random photo" width="24" height="6">

  <nav>
    <article title="with caption">
      <img src="https://picsum.photos/seed/a/160/80" alt="landscape" width="20" height="5">
      <p>A landscape photo</p>
    </article>
    <article title="another">
      <img src="https://picsum.photos/seed/b/160/80" alt="portrait" width="20" height="5">
      <p>A portrait photo</p>
    </article>
  </nav>
</section>`,
  },

  // -- Composition --
  {
    name: "CLI help screen",
    category: "composed",
    format: "html",
    width: 72,
    source: `<section>
  <h2>mycli</h2>
  <p>A blazing fast build tool for the modern web.</p>

  <hr label="usage">
  <pre>
  mycli [command] [options]
  </pre>

  <hr label="commands">

  <table>
    <thead><tr><th>Command</th><th>Description</th></tr></thead>
    <tbody>
      <tr><td>build</td><td>Compile and bundle the project</td></tr>
      <tr><td>dev</td><td>Start development server with HMR</td></tr>
      <tr><td>test</td><td>Run the test suite</td></tr>
      <tr><td>lint</td><td>Check code style and errors</td></tr>
      <tr><td>deploy</td><td>Push to production environment</td></tr>
    </tbody>
  </table>

  <hr label="options">

  <table>
    <thead><tr><th>Flag</th><th>Default</th><th>Description</th></tr></thead>
    <tbody>
      <tr><td>--port</td><td>3000</td><td>Dev server port</td></tr>
      <tr><td>--config</td><td>mycli.config.ts</td><td>Config file path</td></tr>
      <tr><td>--verbose</td><td>false</td><td>Enable debug logging</td></tr>
    </tbody>
  </table>
</section>`,
  },
  {
    name: "Monitoring panel",
    category: "composed",
    format: "html",
    width: 80,
    source: `<section gap="1">
  <nav>
    <article title="cpu">
      <progress label="usr" value="48" max="100"></progress>
      <progress label="sys" value="12" max="100"></progress>
      <progress label="io" value="3" max="100"></progress>
    </article>
    <article title="memory">
      <progress label="used" value="5.6" max="16" show-percent></progress>
      <progress label="cache" value="3.2" max="16" show-percent></progress>
    </article>
    <article title="cluster">
      <p>5/5 healthy</p>
      <p>state: stable</p>
    </article>
  </nav>

  <nav>
    <article title="request latency (ms)">
      <figure height="5" fill color="cyan" axis-color="brightBlack">12,15,11,18,14,22,16,13,19,14,11,17</figure>
    </article>
    <article title="error rate (%)">
      <figure height="5" fill color="red" axis-color="brightBlack">0,1,0,0,2,1,0,0,1,0,0,3</figure>
    </article>
  </nav>

  <table header-color="yellow">
    <thead>
      <tr><th>Service</th><th>Status</th><th>Uptime</th><th>CPU</th><th>Mem</th></tr>
    </thead>
    <tbody>
      <tr><td>api-gateway</td><td>up</td><td>14d 03h</td><td>42%</td><td>1.2G</td></tr>
      <tr><td>auth-service</td><td>up</td><td>14d 03h</td><td>18%</td><td>0.8G</td></tr>
      <tr><td>db-primary</td><td>up</td><td>31d 07h</td><td>67%</td><td>4.1G</td></tr>
      <tr><td>cache-redis</td><td>warn</td><td>23d 11h</td><td>35%</td><td>2.8G</td></tr>
      <tr><td>worker-queue</td><td>up</td><td>07d 12h</td><td>89%</td><td>0.5G</td></tr>
    </tbody>
  </table>

  <footer left=" 4/5 svc healthy | 0.92 Gbps" right="v${VERSION} " bg="#1a1a2e" color="white"></footer>
</section>`,
  },
  {
    name: "Mixed formats",
    category: "composed",
    format: "html",
    width: 72,
    source: `<section gap="1">
  <nav>
    <article title="readme">
      <h3>1ch</h3>
      <p>Terminal-density UI for the web.</p>
      <ul>
        <li>Zero CSS required</li>
        <li>Monospace-native design</li>
        <li>Framework agnostic</li>
      </ul>
      <blockquote>Hand it data, get a UI.</blockquote>
    </article>
    <article title="package.json">
      <table>
        <tbody>
          <tr><th>name</th><td>1ch</td></tr>
          <tr><th>version</th><td>${VERSION}</td></tr>
          <tr><th>type</th><td>module</td></tr>
          <tr><th>main</th><td>dist/index.js</td></tr>
        </tbody>
      </table>
    </article>
  </nav>

  <pre class="language-typescript">
import { registerTermUIElement } from "1ch";
registerTermUIElement();
  </pre>
</section>`,
  },
];

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const STORAGE_KEY = "1ch-repl";
const HASH_PREFIX = "#src=";

type ReplState = {
  source: string;
  format: "html" | "markdown" | "json";
  width: number;
  mode: string;
  exampleIndex: number;
};

function defaultState(): ReplState {
  return {
    source: EXAMPLES[0]!.source,
    format: EXAMPLES[0]!.format,
    width: EXAMPLES[0]!.width,
    mode: "dark",
    exampleIndex: 0,
  };
}

function saveState(state: ReplState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function loadState(): ReplState {
  // URL hash takes precedence
  const hash = decodeStateFromHash();
  if (hash) return hash;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ReplState>;
      if (parsed.source && typeof parsed.source === "string") {
        return {
          source: parsed.source,
          format: (parsed.format as ReplState["format"]) ?? "html",
          width: typeof parsed.width === "number" ? parsed.width : 72,
          mode: parsed.mode ?? "dark",
          exampleIndex: typeof parsed.exampleIndex === "number" ? parsed.exampleIndex : -1,
        };
      }
    }
  } catch {
    // ignore
  }

  return defaultState();
}

// ---------------------------------------------------------------------------
// URL hash sharing
// ---------------------------------------------------------------------------

function utf8ToBase64(str: string): string {
  return btoa(Array.from(new TextEncoder().encode(str), (b) => String.fromCharCode(b)).join(""));
}

function base64ToUtf8(b64: string): string {
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function encodeStateToHash(state: ReplState): string {
  const payload = { s: state.source, f: state.format, w: state.width, m: state.mode };
  return HASH_PREFIX + utf8ToBase64(JSON.stringify(payload));
}

function decodeStateFromHash(): ReplState | null {
  const hash = location.hash;
  if (!hash.startsWith(HASH_PREFIX)) return null;
  try {
    const json = base64ToUtf8(hash.slice(HASH_PREFIX.length));
    const payload = JSON.parse(json) as { s: string; f: string; w: number; m: string };
    if (!payload.s) return null;
    return {
      source: payload.s,
      format: (payload.f as ReplState["format"]) ?? "html",
      width: payload.w ?? 72,
      mode: payload.m ?? "dark",
      exampleIndex: -1,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// DOM refs
// ---------------------------------------------------------------------------

const input = document.querySelector<HTMLTextAreaElement>("#html-input")!;
const widthInput = document.querySelector<HTMLInputElement>("#width-input")!;
const modeSelect = document.querySelector<HTMLSelectElement>("#mode-select")!;
const formatSelect = document.querySelector<HTMLSelectElement>("#format-select")!;
const exampleSelect = document.querySelector<HTMLSelectElement>("#example-select")!;
const renderButton = document.querySelector<HTMLButtonElement>("#render")!;
const resetButton = document.querySelector<HTMLButtonElement>("#reset")!;
const copyHtmlButton = document.querySelector<HTMLButtonElement>("#copy-html")!;
const copyLinkButton = document.querySelector<HTMLButtonElement>("#copy-link")!;
const liveCheckbox = document.querySelector<HTMLInputElement>("#live")!;
const preview = document.querySelector<DemoTermElement>("#preview")!;
const eventLog = document.querySelector<HTMLDivElement>("#event-log")!;

// ---------------------------------------------------------------------------
// Populate example dropdown
// ---------------------------------------------------------------------------

function populateExamples(): void {
  let lastCategory = "";
  let optgroup: HTMLOptGroupElement | null = null;

  const customOpt = document.createElement("option");
  customOpt.value = "-1";
  customOpt.textContent = "(custom)";
  exampleSelect.appendChild(customOpt);

  for (let i = 0; i < EXAMPLES.length; i++) {
    const ex = EXAMPLES[i]!;
    if (ex.category !== lastCategory) {
      optgroup = document.createElement("optgroup");
      optgroup.label = ex.category;
      exampleSelect.appendChild(optgroup);
      lastCategory = ex.category;
    }
    const opt = document.createElement("option");
    opt.value = String(i);
    opt.textContent = ex.name;
    optgroup!.appendChild(opt);
  }
}

populateExamples();

// ---------------------------------------------------------------------------
// Render logic
// ---------------------------------------------------------------------------

let renderTimer: ReturnType<typeof setTimeout> | undefined;
let currentState: ReplState = loadState();

function sanitizeWidth(raw: string): number {
  const value = Number.parseInt(raw, 10);
  if (!Number.isFinite(value)) return 72;
  return Math.max(20, Math.min(140, value));
}

function actionText(action: HtmlAction): string {
  if (action.type === "link") return `link ${action.href ?? action.text}`;
  return `action ${action.id ?? action.text}`;
}

function applyState(state: ReplState): void {
  currentState = state;
  input.value = state.source;
  widthInput.value = String(state.width);
  modeSelect.value = state.mode;
  formatSelect.value = state.format;
  exampleSelect.value = String(state.exampleIndex);
  renderPreview();
}

function renderPreview(): void {
  const width = sanitizeWidth(widthInput.value);
  widthInput.value = String(width);
  currentState.width = width;
  currentState.mode = modeSelect.value;
  currentState.format = formatSelect.value as ReplState["format"];
  currentState.source = input.value;

  preview.setAttribute("width", String(width));
  preview.setAttribute("mode", currentState.mode);
  preview.setAttribute("source-format", currentState.format);
  preview.setAttribute("source", input.value);
  preview.render();

  saveState(currentState);
}

function queueLiveRender(): void {
  if (!liveCheckbox.checked) return;
  if (renderTimer) clearTimeout(renderTimer);
  renderTimer = setTimeout(() => {
    renderTimer = undefined;
    renderPreview();
  }, 120);
}

// ---------------------------------------------------------------------------
// Event handlers
// ---------------------------------------------------------------------------

input.addEventListener("input", () => {
  // User edited -- mark as custom unless it still matches the example
  if (currentState.exampleIndex >= 0) {
    const ex = EXAMPLES[currentState.exampleIndex];
    if (!ex || input.value !== ex.source) {
      currentState.exampleIndex = -1;
      exampleSelect.value = "-1";
    }
  }
  queueLiveRender();
});

widthInput.addEventListener("input", queueLiveRender);
modeSelect.addEventListener("change", renderPreview);

formatSelect.addEventListener("change", () => {
  currentState.format = formatSelect.value as ReplState["format"];
  renderPreview();
});

exampleSelect.addEventListener("change", () => {
  const idx = Number.parseInt(exampleSelect.value, 10);
  if (idx < 0 || idx >= EXAMPLES.length) return;
  const ex = EXAMPLES[idx]!;
  currentState.exampleIndex = idx;
  currentState.source = ex.source;
  currentState.format = ex.format;
  currentState.width = ex.width;
  input.value = ex.source;
  formatSelect.value = ex.format;
  widthInput.value = String(ex.width);
  renderPreview();
});

renderButton.addEventListener("click", renderPreview);

resetButton.addEventListener("click", () => {
  const state = defaultState();
  applyState(state);
});

copyHtmlButton.addEventListener("click", () => {
  navigator.clipboard.writeText(input.value).then(
    () => flash(copyHtmlButton, "copied!"),
    () => flash(copyHtmlButton, "failed"),
  );
});

copyLinkButton.addEventListener("click", () => {
  const hash = encodeStateToHash(currentState);
  const url = `${location.origin}${location.pathname}${hash}`;
  navigator.clipboard.writeText(url).then(
    () => flash(copyLinkButton, "copied!"),
    () => flash(copyLinkButton, "failed"),
  );
});

preview.addEventListener("termui-action", (event) => {
  const action = (event as CustomEvent<HtmlAction>).detail;
  eventLog.innerHTML = `<strong>${actionText(action)}</strong>`;
});

// Tab key support in textarea
input.addEventListener("keydown", (e) => {
  if (e.key === "Tab") {
    e.preventDefault();
    const start = input.selectionStart;
    const end = input.selectionEnd;
    input.value = input.value.substring(0, start) + "  " + input.value.substring(end);
    input.selectionStart = input.selectionEnd = start + 2;
    queueLiveRender();
  }
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flash(button: HTMLButtonElement, text: string): void {
  const original = button.textContent;
  button.textContent = text;
  setTimeout(() => {
    button.textContent = original;
  }, 1200);
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

applyState(currentState);

// Listen for hash changes (e.g. shared links)
window.addEventListener("hashchange", () => {
  const hash = decodeStateFromHash();
  if (hash) applyState(hash);
});
